import { commercialOptions, crmOptions, gestaoOptions, options, HeaderOption } from "../models/Home";

export interface ScreenModule {
  title: string;
  hint?: string;
  screens: HeaderOption[];
}

// Telas que podem ter o acesso controlado por usuário, agrupadas por módulo
// (vários módulos têm telas com o mesmo nome, ex.: "Dashboard" principal e do CRM).
// Usamos as mesmas opções exibidas na sidebar como fonte da verdade.
export const screenModules: ScreenModule[] = [
  {
    title: "Principal",
    screens: options.filter((screen) => screen.link !== "/commercial" && screen.link !== "/gestao/painel" && screen.link !== "/intranet"),
  },
  {
    title: "Intranet",
    hint: "Sempre explícito, inclusive para admin",
    screens: options.filter((screen) => screen.link === "/intranet"),
  },
  {
    title: "Comercial",
    hint: "Sempre explícito, inclusive para admin",
    screens: commercialOptions.filter((screen) => screen.link !== "/commercial/crm/leads"),
  },
  { title: "CRM", hint: "Sempre explícito, inclusive para admin", screens: crmOptions },
  { title: "Gestão", screens: gestaoOptions },
];

export const accessibleScreenOptions: HeaderOption[] = screenModules.flatMap((module) => module.screens);

// Telas permitidas ao cliente (position 0/1). Mesmo que alguém conceda mais, o cliente fica limitado a elas.
const defaultClientScreens = ["/dashboard", "/documents", "/processes", "/financial"];

const isCommercialScreen = (link: string) =>
  link === "/commercial" || link.startsWith("/commercial/");

// Comercial/CRM e Intranet: só com grant explícito (PageAccessHandler.IsExplicit)
const isExplicitScreen = (link: string) => isCommercialScreen(link) || link === "/intranet";

/**
 * Decide se o usuário autenticado pode acessar determinada tela (pelo link).
 * Espelha `PageAccessHandler.CanAccessAsync` no backend.
 *
 * Regras:
 * - cliente (position 0/1): nunca Comercial/CRM; demais só `defaultClientScreens`
 *   (e, se `screens` estiver configurado, só as que estiverem nele);
 * - Comercial/CRM e Intranet são sempre explícitos, inclusive para admin: precisa estar em `screens`
 *   (o hub `/commercial` libera se houver qualquer tela `/commercial/...`);
 * - position === 2 (super admin) e admins enxergam todo o resto;
 * - sem telas não-comerciais configuradas => comportamento legado, libera tudo;
 * - caso contrário, só libera as telas presentes em `screens`.
 */
export const hasScreenAccess = (auth: any, link: string): boolean => {
  if (!auth) return false;

  const screens: string[] = auth.screens ?? [];
  const isExternalClient = auth.position === 0 || auth.position === 1;

  if (isExternalClient) {
    if (isExplicitScreen(link)) return false;
    return defaultClientScreens.includes(link) && (screens.length === 0 || screens.includes(link));
  }

  if (isExplicitScreen(link)) {
    if (link === "/commercial") return screens.some((screen) => screen.startsWith("/commercial/"));
    return screens.includes(link);
  }

  if (auth.position === 2 || auth.admin) return true;

  const generalScreens = screens.filter((screen) => !isExplicitScreen(screen));
  if (generalScreens.length === 0) return true;

  return screens.includes(link);
};
